import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, CalendarClock, Send, CheckCircle2, Image as ImageIcon, Lightbulb } from 'lucide-react';
import api from '../services/apiClient';
import { Button, Input, AutoTextarea, StatusBadge } from '../components/ui';
import { toast } from '../components/toast';
import ImageModal from '../components/ImageModal';
import Lightbox from '../components/Lightbox';
import { NETWORK_LABELS, NETWORK_ICONS, NETWORK_COLORS } from '../lib/networks';

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function PostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [targets, setTargets] = useState([]);
  const [images, setImages] = useState({});
  const [scheduledAt, setScheduledAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [imageModalFor, setImageModalFor] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/posts/${id}`);
      setPost(data);
      setTargets(data.targets || []);
      setScheduledAt(toLocalInput(data.scheduledAt));
      const imgs = {};
      (data.targets || []).forEach((t) => { imgs[t.id] = t.media || []; });
      setImages(imgs);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setTargetText = (tid, text) => setTargets((ts) => ts.map((t) => (t.id === tid ? { ...t, text } : t)));

  const saveTexts = async () => {
    await api.put(`/posts/${id}`, { targets: targets.map((t) => ({ id: t.id, text: t.text, hashtags: t.hashtags })) });
  };

  const save = async () => {
    setBusy(true);
    try { await saveTexts(); toast.success('Alteracoes salvas'); await load(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try { await saveTexts(); await api.post(`/posts/${id}/approve`); toast.success('Aprovado'); await load(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const schedule = async () => {
    if (!scheduledAt) return toast.error('Escolha data e hora');
    setBusy(true);
    try {
      await saveTexts();
      const res = await api.post(`/posts/${id}/schedule`, { scheduledAt: new Date(scheduledAt).toISOString() });
      const fails = (res.results || []).filter((r) => !r.ok);
      if (fails.length) toast.error(`Agendado com falhas: ${fails.map((f) => f.network).join(', ')}`);
      else toast.success('Agendado no Metricool!');
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const publishNow = async () => {
    setBusy(true);
    try { await saveTexts(); await api.post(`/posts/${id}/publish-now`); toast.success('Enviado ao Metricool'); await load(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const onImageChange = (tid, mediaArr) => {
    setImages((m) => ({ ...m, [tid]: mediaArr || [] }));
  };

  if (loading) return <p className="muted">Carregando…</p>;
  if (!post) return null;

  const published = post.status === 'published';

  return (
    <div>
      <div className="row between">
        <div className="row" style={{ gap: 10 }}>
          <Button variant="ghost" className="sm" onClick={() => navigate('/calendario')}><ArrowLeft size={16} /> Voltar</Button>
          <h1 className="page-title" style={{ margin: 0 }}>{post.title || 'Post'}</h1>
          <StatusBadge status={post.status} />
        </div>
        <Button variant="secondary" onClick={save} loading={busy}><Save size={15} /> Salvar</Button>
      </div>

      {post.brief && <p className="muted mb16">{post.brief}</p>}

      <div className="card" style={{ maxWidth: 860 }}>
        {targets.map((t) => {
          const Icon = NETWORK_ICONS[t.network];
          const media = images[t.id] || [];
          return (
            <div key={t.id} className="gen-card">
              <div className="gen-head">
                <span className="net-name">
                  <Icon size={17} color={NETWORK_COLORS[t.network]} /> {NETWORK_LABELS[t.network]}
                </span>
                <div className="row" style={{ gap: 8 }}>
                  <span className="muted" style={{ fontSize: 12 }}>{(t.text || '').length} caracteres</span>
                  {!published && (
                    <Button variant="secondary" className="sm" onClick={() => setImageModalFor(t.id)}>
                      <ImageIcon size={14} /> {media.length ? `Imagens (${media.length})` : 'Adicionar imagem'}
                    </Button>
                  )}
                </div>
              </div>
              <div className="gen-body">
                <AutoTextarea value={t.text || ''} onChange={(e) => setTargetText(t.id, e.target.value)} disabled={published} />
                {media.length > 0 && (
                  <div className="gen-thumbs">
                    {media.map((m, i) => (
                      <button key={m.key} type="button" className="gen-thumb" onClick={() => setLightbox({ images: media.map((x) => x.url), index: i })} title="Ver ampliada">
                        <img src={m.url} alt="Imagem do post" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {t.imageSuggestion && (
                <div className="suggestion-box">
                  <div className="sb-head"><Lightbulb size={12} /> Sugestão de imagem/carrossel (não vai no post)</div>
                  {t.imageSuggestion}
                </div>
              )}
            </div>
          );
        })}

        {!published && (
          <div className="row wrap mt16" style={{ alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 5 }}>Agendar para</label>
              <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
            </div>
            {post.status === 'draft' && <Button variant="secondary" onClick={approve} loading={busy}><CheckCircle2 size={15} /> Aprovar</Button>}
            <Button onClick={schedule} loading={busy}><CalendarClock size={15} /> Agendar</Button>
            <Button variant="secondary" onClick={publishNow} loading={busy}><Send size={15} /> Publicar agora</Button>
          </div>
        )}
      </div>

      {imageModalFor && (() => {
        const t = targets.find((x) => x.id === imageModalFor);
        if (!t) return null;
        return (
          <ImageModal
            targetId={t.id}
            network={t.network}
            currentMedia={images[t.id] || []}
            onChange={(mediaArr) => onImageChange(t.id, mediaArr)}
            onClose={() => setImageModalFor(null)}
          />
        );
      })()}

      <Lightbox images={lightbox?.images} index={lightbox?.index || 0} onClose={() => setLightbox(null)} />
    </div>
  );
}
